import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useSeo } from "../useSeo";
import { trackEvent } from "../trackEvent";
import { VENUE } from "../data";
import ChannelsList from "../ChannelsList";
import { ChevronRight } from "lucide-react";
import { API_BASE } from "../../api/client";
import { nextOccurrenceOnOrAfter } from "../eventOccurrences";
import EBOOKS_MOCK from "../../data/ebooks.json";

// Destaques de evento -- banners+CTA na home, trocados manualmente (imagens em
// public/institucional/) conforme os proximos eventos em foco. Cada banner
// tem a data do evento (eventDate) e uma data de expiracao (expiresAt): so
// entra no destaque a partir de FEATURED_WINDOW_DAYS dias antes do evento, e
// some sozinho da home assim que o horario passa -- nao depende de alguem
// lembrar de tirar manualmente nem de cadastrar cedo demais (evita banners
// de eventos distantes ficando visiveis meses antes). Tamanho reduzido
// (maxWidth) de proposito, pra nao dominar a home com duas imagens verticais
// lado a lado.
// Sem mecanismo de "evento destacado" no banco -- e so esta lista, ajustada
// aqui quando os eventos em destaque mudarem (adicionar/remover objetos e
// suas imagens/datas).
const FEATURED_WINDOW_DAYS = 15;

// Quanto tempo depois do horario real de inicio do evento o banner continua
// aparecendo na home antes de sumir sozinho (pedido do Rodrigo: o culto das
// 10h da manha ainda aparecia como "Hoje" a noite, horas depois de ter
// acabado). 3h cobre cultos/encontros que se estendem um pouco alem do
// horario de inicio sem deixar o banner velho na home o dia inteiro.
const EVENT_VISIBLE_AFTER_START_HOURS = 3;

// Teto de banners em destaque na home. Cada card tem flex: "1 1 220px" e
// quebra de linha quando nao cabe mais um ao lado -- com 5+ banners, o
// ultimo sobra sozinho numa segunda linha (reportado pelo Rodrigo: "o
// volume de banner deveria ser 4 pra nao quebrar em 2 linhas"). 4 e' o
// quanto cabe numa linha so nas larguras de tela mais comuns sem sobrar
// ninguem; os mais distantes no tempo (ja ordenados por sortTime) ficam
// de fora ate os mais proximos acontecerem e abrirem vaga.
const MAX_FEATURED_BANNERS = 4;

// `ratio` = largura/altura real de cada imagem (medida no arquivo), usada
// pra reservar a altura certa do card ANTES da imagem carregar -- sem isso
// o card nasce sem altura (so a borda/padding), e quando a imagem termina de
// baixar o card "pula" pro tamanho certo, empurrando o resto da home (esse
// pulo ficava mais visivel ao voltar pra home depois de navegar pra outra
// pagina, porque o componente desmonta/remonta e as imagens recarregam do
// zero). Os banners aqui tem proporcoes bem diferentes entre si (tem um
// vertical e o resto horizontal), entao precisa ser por imagem -- um unico
// aspect-ratio fixo pra todo mundo ia distorcer ou cortar os que nao
// batessem com esse valor.
const FEATURED_EVENTS = [
  {
    id: "jovens_26set",
    to: "/eventos",
    image: "/institucional/mosaico-jovens-encontro-set2026-v2.jpg",
    alt: "Mosaico Jovens · Encontro Deus Pai, 26 de setembro (sábado) às 19h30, Av. dos Autonomistas 2233, Osasco",
    ratio: 720 / 1280,
    eventDate: "2026-09-26T19:30:00-03:00"
  },
  {
    id: "culto_04out",
    to: "/eventos",
    image: "/institucional/mosaico-culto-modo-aviao-out2026-v2.jpg",
    alt: "Modo Avião · O Reino do Avesso, 4 de outubro (domingo), café 9h e celebração 10h, Av. dos Autonomistas 2233, Osasco",
    ratio: 1600 / 900,
    eventDate: "2026-10-04T10:00:00-03:00"
  },
  {
    id: "culto_11out",
    to: "/eventos",
    image: "/institucional/mosaico-culto-a-piada-era-verdade-11out2026.jpg",
    alt: "A Piada Era Verdade, 11 de outubro (domingo), café da manhã 9h e celebração 10h, Av. dos Autonomistas 2233, Osasco",
    ratio: 1672 / 941,
    eventDate: "2026-10-11T10:00:00-03:00"
  },
  {
    id: "jovens_17out",
    to: "/eventos",
    image: "/institucional/mosaico-jovens-encontro-deus-filho-17out2026.jpg",
    alt: "Mosaico Jovens · Encontro Deus Filho — O amor que nos faz pertencer, 17 de outubro (sábado) às 19h30, Av. dos Autonomistas 2233, Osasco",
    ratio: 1672 / 941,
    eventDate: "2026-10-17T19:30:00-03:00"
  },
  {
    id: "culto_18out",
    to: "/eventos",
    image: "/institucional/mosaico-culto-digite-amem-18out2026.jpg",
    alt: "Digite Amém se Você Crê, 18 de outubro (domingo), café da manhã 9h e celebração 10h, Av. dos Autonomistas 2233, Osasco",
    ratio: 638 / 360,
    eventDate: "2026-10-18T10:00:00-03:00"
  },
  {
    id: "culto_25out",
    to: "/eventos",
    image: "/institucional/mosaico-culto-25out2026.jpg",
    alt: "Igreja Batista Mosaico, domingo 25 de outubro, café da manhã 9h e celebração 10h, Av. dos Autonomistas 2233, Osasco",
    ratio: 638 / 360,
    eventDate: "2026-10-25T10:00:00-03:00"
  },
  {
    id: "encontro_mulheres_31out",
    to: "/eventos",
    image: "/institucional/mosaico-encontro-mulheres-31out2026.jpg",
    alt: "Encontro das Mulheres, 31 de outubro (sábado) às 16h, Av. dos Autonomistas 2233, Osasco",
    ratio: 1600 / 900,
    eventDate: "2026-10-31T16:00:00-03:00"
  },
  {
    id: "culto_01nov",
    to: "/eventos",
    image: "/institucional/mosaico-culto-de-dentro-pra-fora-01nov2026.jpg",
    alt: "De Dentro Pra Fora, 1º de novembro (domingo), café da manhã 9h e celebração 10h, Av. dos Autonomistas 2233, Osasco",
    // arte trocada (19/10/2026) por uma versao 16:9 ja composta certo (texto
    // com folga da borda) -- nao precisa mais do "padded" (ver comentario no
    // render abaixo, que so se aplica quando evento.padded existir).
    ratio: 1600 / 900,
    eventDate: "2026-11-01T10:00:00-03:00"
  },
  {
    id: "jovens_14nov",
    to: "/eventos",
    image: "/institucional/mosaico-jovens-encontro-deus-espirito-santo-14nov2026.jpg",
    alt: "Mosaico Jovens · Encontro Deus Espírito Santo, 14 de novembro (sábado) às 19h30, Av. dos Autonomistas 2233, Osasco",
    ratio: 1671 / 941,
    eventDate: "2026-11-14T19:30:00-03:00"
  },
  {
    id: "culto_08nov",
    to: "/eventos",
    image: "/institucional/mosaico-culto-herege-08nov2026.png",
    alt: "Herege — uma nova série, 8 de novembro (domingo), café da manhã 9h e celebração 10h, Av. dos Autonomistas 2233, Osasco",
    ratio: 1600 / 900,
    eventDate: "2026-11-08T10:00:00-03:00"
  }
];

// Banners recorrentes -- ao contrario dos banners acima (imagem+expiresAt
// fixos, trocados manualmente por evento), estes sao calculados a partir da
// agenda publica (/events/public): cada um entra sozinho no destaque quando
// faltam daysBefore dias ou menos para a proxima ocorrencia de eventTitle, e
// some assim que a data passar -- sem precisar editar nada a cada repeticao
// (pedido original: Cafe Sem Filtro e Escola Biblica repetem a cada 15 dias
// e nao dava pra ficar atualizando o banner toda vez). eventTitle precisa
// bater exatamente com o title cadastrado em /eventos.
const RECURRING_FEATURED_BANNERS = [
  {
    id: "cafe_sem_filtro",
    eventTitle: "Encontro de Homens",
    to: "/eventos",
    image: "/institucional/cafe-sem-filtro-encontro-homens.jpg",
    alt: "Café Sem Filtro — Bate-papo dos Homens, às segundas-feiras a cada 15 dias, às 20h",
    ratio: 1920 / 1080,
    daysBefore: 7
  },
  {
    id: "escola_biblica",
    eventTitle: "Escola Bíblica",
    to: "/eventos",
    image: "/institucional/escola-biblica-banner-v2.jpg",
    alt: "Escola Bíblica, a cada 15 dias às 20h, Av. dos Autonomistas 2233, Osasco",
    ratio: 1920 / 1080,
    daysBefore: 7
  },
  {
    id: "pg_dos_jovens",
    // Era "PG dos Jovens" (nome antigo) -- o evento cadastrado em
    // /admin/eventos foi renomeado pra "Encontro dos Jovens — noite de
    // louvor e adoração" (ver commit 0ae29a1, 16/09/2026) e este
    // eventTitle nunca foi atualizado junto, entao o banner nunca mais
    // batia com nenhum evento real (sempre ficava de fora do destaque).
    // Corrigido 28/10/2026, achado pela Carla ("cade o PG dos jovens").
    eventTitle: "Encontro dos Jovens — noite de louvor e adoração",
    to: "/eventos",
    image: "/institucional/pg-dos-jovens.jpg",
    alt: "Encontro dos Jovens — todo sábado às 19h30, Igreja Batista Mosaico",
    ratio: 1672 / 941,
    daysBefore: 7
  }
];

// Texto curto pra etiqueta de cima do card (badge), reaproveitando dado que
// ja existe no cadastro do evento (timeLabel, ex.: "Qui · 20h") -- nao
// inventa nada novo, so troca por "Hoje"/"Amanhã" quando a ocorrencia
// esta bem perto, que escaneia mais rapido que o dia da semana.
function relativeBadge(daysUntil, dateLabel, timeText) {
  const prefix = daysUntil === 0 ? "Hoje" : daysUntil === 1 ? "Amanhã" : dateLabel;
  if (!prefix) return null;
  return timeText ? `${prefix} · ${timeText}` : prefix;
}

// Etiqueta "Seg · 06/10" pros banners (fixos e recorrentes). Antes os
// recorrentes usavam o timeLabel cadastrado no evento (ex.: "Qui · 20h") e os
// fixos (FEATURED_EVENTS) so tinham a data (ver formatShortDate original) --
// Rodrigo notou a inconsistencia (um so com data, outros so com dia+hora) e
// pediu pra unificar em dia da semana + data em todos. Dado que ja existe
// (eventDate nos fixos, a propria ocorrencia calculada nos recorrentes), so
// reformata.
const WEEKDAY_ABBR = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function formatBadgeDate(dateInput) {
  const d = new Date(dateInput);
  const day = String(d.getUTCDate()).padStart(2, "0");
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${WEEKDAY_ABBR[d.getUTCDay()]} · ${day}/${month}`;
}

// Le so a data (ano/mes/dia) de uma string ISO tipo "2026-10-04T10:00:00-03:00"
// na marra, sem passar por Date/getUTC* -- um evento marcado pra 23h-03:00,
// por exemplo, vira 02h UTC do dia SEGUINTE, e isso empurraria a "data" do
// evento (e o calculo de dias-ate-o-evento) pro dia errado se a gente usasse
// getUTCDate() direto em cima do horario real.
function dateOnlyFromIso(isoString) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoString);
  if (!match) return null;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

// Horario (ex.: "10h", "19h30") direto da mesma string ISO acima, pra exibir
// no badge -- o offset "-03:00" na string ja e o horario de Brasilia certo,
// entao le as horas/minutos na marra em vez de converter o Date pra UTC.
function extractTimeLabel(isoString) {
  const match = /T(\d{2}):(\d{2})/.exec(isoString);
  if (!match) return null;
  const hour = String(Number(match[1]));
  const minute = match[2];
  return minute === "00" ? `${hour}h` : `${hour}h${minute}`;
}

// Separa o horario (ex.: "20h") do timeLabel cadastrado no evento (ex.:
// "Qui · 20h", ver Eventos.jsx) pra reaproveitar no badge e no calculo de
// quando o banner recorrente deve sumir, sem pedir recadastro de nada.
// Busca o padrao "<numero>h<minutos opcionais>" EM QUALQUER PARTE da string
// (nao so' depois do ultimo "·") -- a primeira versao exigia bater exatamente
// o trecho apos o ultimo "·", e qualquer coisa fora desse formato exato (ex.:
// texto extra depois da hora, ou sem "·" nenhum) fazia o horario sumir do
// banner em silencio (caso real: "Encontro de Homens", reportado pelo
// Rodrigo vendo o badge do Café Sem Filtro sem horario).
function extractTimeFromLabel(timeLabel) {
  if (!timeLabel) return null;
  const match = /(\d{1,2})h(\d{2})?\b/.exec(String(timeLabel));
  if (!match) return null;
  const text = match[2] ? `${match[1]}h${match[2]}` : `${match[1]}h`;
  return { hour: Number(match[1]), minute: match[2] ? Number(match[2]) : 0, text };
}

// Abre o chat do Barna embutido no site (widget.js, ver
// mosaico-api/src/agent/widget/widget.js) -- NAO e' o mesmo link que
// ChannelsList usa pro numero de WhatsApp dedicado do Barna (wa.me/...): esse
// card aqui foca em quem ja esta no site e nao precisa sair pro WhatsApp pra
// falar com o agente (pedido do Rodrigo, 10/2026). O widget e' isolado em
// Shadow DOM de proposito e nao expoe nada do seu DOM interno -- em vez de
// mexer nele na marra, so dispara um evento global que o proprio widget.js
// escuta. Se o widget ainda nao tiver terminado de carregar (ele e' injetado
// de forma assincrona, ver public/index.html), marca uma flag que o
// widget.js confere e consome assim que termina de montar, sem perder o
// clique.
function openBarnabeWidget() {
  if (typeof window === "undefined") return;
  if (!window.__mosaicoAgente) window.__mosaicoAgenteAbrirPendente = true;
  document.dispatchEvent(new CustomEvent("mosaico-agente:abrir"));
}

// Cache em memoria (dura so a sessao da aba, reseta com F5) do resultado do
// fetch de /events/public usado pros banners recorrentes -- sem isso, toda
// vez que o componente Home monta (inclusive so voltando pra tela, ja que o
// React Router desmonta/remonta a pagina ao navegar) a lista recomecava
// vazia e so ganhava os banners recorrentes um instante depois, quando o
// fetch terminava -- um "pulo" visivel de 2 pra 4 banners, com os 2 ja
// visiveis mudando de posicao (empurrados pelo justifyContent: center) assim
// que os outros 2 apareciam (reportado pelo Rodrigo: "ao clicar no inicio da
// home ta pulando os banners aparece 2 depois carrega os 4"). Com o cache, a
// partir da segunda vez que a Home monta na mesma sessao os banners
// recorrentes ja aparecem de primeira, sem pulo nenhum -- so a primeirissima
// visita da sessao ainda tem esse instante de carregamento (inevitavel sem
// isso vir pronto do servidor).
let recurringBannersCache = null;

export default function Home() {
  const todayFloorMs = (() => {
    const now = new Date();
    return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  })();

  const featuredEvents = FEATURED_EVENTS.filter((evento) => {
    const now = Date.now();
    if (evento.eventDate) {
      const eventTimeMs = new Date(evento.eventDate).getTime();
      const expiresAt = eventTimeMs + EVENT_VISIBLE_AFTER_START_HOURS * 3600000;
      if (now >= expiresAt) return false;
      const showFrom = eventTimeMs - FEATURED_WINDOW_DAYS * 86400000;
      if (now < showFrom) return false;
    }
    return true;
  }).map((evento) => {
    // Mesma logica de etiqueta "Hoje"/"Amanha"/data+hora dos banners
    // recorrentes (relativeBadge acima) -- esses banners fixos nao tinham
    // etiqueta nenhuma antes (reportado pelo Rodrigo ao ver o banner do
    // culto sem data nenhuma em cima, depois sem horario).
    let badge = null;
    let isToday = false;
    if (evento.eventDate) {
      const eventFloorMs = dateOnlyFromIso(evento.eventDate);
      const daysUntil = Math.round((eventFloorMs - todayFloorMs) / 86400000);
      isToday = daysUntil === 0;
      badge = relativeBadge(daysUntil, formatBadgeDate(eventFloorMs), extractTimeLabel(evento.eventDate));
    }
    return {
      ...evento,
      badge,
      isToday,
      // Pra entrar na mesma ordenacao dos banners recorrentes abaixo.
      sortTime: evento.eventDate ? new Date(evento.eventDate).getTime() : Infinity
    };
  });

  // Busca a agenda publica so pra decidir se algum RECURRING_FEATURED_BANNERS
  // deve entrar no destaque agora (ver comentario acima da constante). Falha
  // de rede so significa que esses banners extras nao aparecem -- o resto da
  // home (incluindo os banners fixos de FEATURED_EVENTS) continua normal.
  const [recurringBanners, setRecurringBanners] = useState(recurringBannersCache || []);

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/events/public`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("falha ao buscar eventos"))))
      .then((data) => {
        if (!alive || !Array.isArray(data)) return;
        const today = new Date();
        const todayFloor = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
        const matches = RECURRING_FEATURED_BANNERS.map((banner) => {
          // O mesmo titulo pode ter mais de uma linha no banco -- editar a
          // data de uma serie recorrente (admin de /eventos) cria uma linha
          // nova em vez de so atualizar a existente, deixando a antiga pra
          // tras (encerrada, sem recurrenceIntervalDays). Usar so a primeira
          // que aparecer no array (find) travava o banner pra sempre quando
          // essa primeira linha era a antiga/encerrada -- caso real:
          // "Encontro de Homens" sumiu do destaque depois de mudar o dia de
          // quinta pra segunda. Em vez disso, olha todas as linhas com esse
          // titulo e pega a ocorrencia futura mais proxima entre elas.
          const candidates = data.filter((e) => e.title === banner.eventTitle);
          let event = null;
          let occurrence = null;
          for (const candidate of candidates) {
            const candidateOccurrence = nextOccurrenceOnOrAfter(candidate, todayFloor);
            if (candidateOccurrence && (!occurrence || candidateOccurrence < occurrence)) {
              occurrence = candidateOccurrence;
              event = candidate;
            }
          }
          if (!event || !occurrence) return null;
          const daysUntil = Math.round((occurrence.getTime() - todayFloor.getTime()) / 86400000);
          if (daysUntil > banner.daysBefore || daysUntil < 0) return null;
          // occurrence e meia-noite UTC do dia do evento; o horario real
          // (timeLabel, ex.: "Qui · 20h") e em horario de Brasilia (UTC-3),
          // por isso soma 3h ao converter pra UTC antes de comparar com agora.
          const timeInfo = extractTimeFromLabel(event.timeLabel);
          const eventTimeMs = timeInfo
            ? occurrence.getTime() + (timeInfo.hour + 3) * 3600000 + timeInfo.minute * 60000
            : occurrence.getTime();
          const expiresAt = eventTimeMs + EVENT_VISIBLE_AFTER_START_HOURS * 3600000;
          if (Date.now() >= expiresAt) return null;
          return {
            id: banner.id,
            to: banner.to,
            image: banner.image,
            alt: banner.alt,
            ratio: banner.ratio,
            sortTime: occurrence.getTime(),
            isToday: daysUntil === 0,
            badge: relativeBadge(daysUntil, formatBadgeDate(occurrence), timeInfo ? timeInfo.text : null)
          };
        }).filter(Boolean);
        recurringBannersCache = matches;
        setRecurringBanners(matches);
      })
      .catch(() => {
        // Mantem so os banners fixos em caso de falha -- nunca quebra a home.
      });
    return () => {
      alive = false;
    };
  }, []);

  // Banner "ao vivo agora" -- so aparece quando o culto de domingo ou o
  // podcast Fe e Cafe (sextas) estao mesmo sendo transmitidos (pedido do
  // Rodrigo: "aparecer so se estiver on"). A checagem em si (qual janela de
  // horario esta ativa, se o canal do pastor esta em live) e toda feita no
  // backend (/api/live-status, ver mosaico-api/src/routes/live.js), que
  // devolve tambem liveStatus.kind ("culto" | "podcast") -- o front so faz
  // polling leve, troca o texto conforme o kind, e esconde o banner se der
  // qualquer erro, sem nunca quebrar a home por isso.
  const [liveStatus, setLiveStatus] = useState(null);

  useEffect(() => {
    let alive = true;
    const checkLive = () => {
      fetch(`${API_BASE}/live-status`)
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error("falha ao checar live"))))
        .then((data) => {
          if (!alive) return;
          setLiveStatus(data && data.live ? data : null);
        })
        .catch(() => {
          if (alive) setLiveStatus(null);
        });
    };
    checkLive();
    const intervalId = setInterval(checkLive, 60000);
    return () => {
      alive = false;
      clearInterval(intervalId);
    };
  }, []);

  const allFeaturedEvents = featuredEvents
    .concat(recurringBanners)
    .sort((a, b) => a.sortTime - b.sortTime)
    .slice(0, MAX_FEATURED_BANNERS);

  // Arte ampliada de qualquer banner clicado -- todos abrem assim (pedido do
  // Rodrigo) em vez de navegar pra /eventos, que so lista os recorrentes e
  // nunca traz nada especifico sobre o banner em si.
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    if (!lightboxImage) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setLightboxImage(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [lightboxImage]);

  useSeo({
    title: "Igreja Batista Mosaico — Igreja em Osasco, SP | Cultos aos domingos",
    description: "Uma igreja em Osasco (SP) que aposta em comunhão de verdade — evangelho sem glamour. Cultos aos domingos, 9h café da manhã e 10h celebração.",
    path: "/"
  });

  return (
    <>
      <div className="hero">
        <div className="hero-grid">
          <div className="hero-copy">
            <p className="eyebrow">Igreja Batista Mosaico</p>
            <h1>Uma igreja. Uma família. Um propósito.</h1>
            <p className="lead">Uma comunidade em Osasco (SP) que aposta em comunhão de verdade — evangelho sem glamour, para quem já cansou de estrutura, hierarquia e distância entre liderança e povo.</p>
            <div className="hero-ctas">
              <NavLink className="primary" to="/sobre" onClick={() => trackEvent("cta_click", { cta: "como_visitar", location: "hero" })}>Como visitar</NavLink>
              <NavLink className="secondary" to="/familia" onClick={() => trackEvent("cta_click", { cta: "acessar_familia", location: "hero" })}>Acessar Família Mosaico</NavLink>
              <NavLink className="tertiary" to="/contato#give-card-section" onClick={() => trackEvent("cta_click", { cta: "contribuir", location: "hero" })}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.8 8.6c0 4.6-6.2 8.4-8.8 10.1-2.6-1.7-8.8-5.5-8.8-10.1a4.9 4.9 0 0 1 8.8-3 4.9 4.9 0 0 1 8.8 3Z" /></svg>
                Contribuir
              </NavLink>
            </div>
            <div className="hero-schedule">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></svg>
              <span><strong>Domingo</strong> · 9h Café da manhã · 10h Celebração</span>
            </div>
          </div>
          <div className="hero-media">
            <img src={VENUE.photoHome} alt="Entrada da Igreja Batista Mosaico, em Osasco (SP)" />
          </div>
        </div>
      </div>

      {liveStatus && (() => {
        const isPodcast = liveStatus.kind === "podcast";
        const badgeLabel = isPodcast ? "Podcast ao vivo agora" : "Culto ao vivo agora";
        const fallbackTitle = isPodcast ? "Fé e Café" : "Culto de domingo";
        return (
          <a
            href={liveStatus.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("cta_click", { cta: isPodcast ? "podcast_ao_vivo" : "culto_ao_vivo", location: "home_live_banner" })}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              maxWidth: 640,
              margin: "28px auto 0",
              padding: "12px 16px",
              borderRadius: 14,
              background: "rgba(214,48,49,0.12)",
              border: "1px solid rgba(214,48,49,0.4)",
              textDecoration: "none",
              color: "inherit"
            }}
          >
            {liveStatus.thumbnailUrl && (
              <img
                src={liveStatus.thumbnailUrl}
                alt=""
                style={{ width: 96, height: 54, objectFit: "cover", borderRadius: 8, flexShrink: 0 }}
              />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: "#d63031",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  padding: "3px 9px",
                  borderRadius: 999,
                  marginBottom: 6
                }}
              >
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff" }} />
                {badgeLabel}
              </span>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {liveStatus.title || fallbackTitle}
              </p>
              <p style={{ margin: "2px 0 0", fontSize: 12, opacity: 0.75 }}>Assistir agora no YouTube →</p>
            </div>
          </a>
        );
      })()}

      {(() => {
        const featuredEbook = EBOOKS_MOCK.find((ebook) => ebook.featuredHome);
        if (!featuredEbook) return null;
        return (
          <NavLink
            // Antes ia pra "/familia" generico (home logada), ignorando o ebook em
            // destaque -- agora vai direto pra landing/leitura desse ebook
            // especifico (mesma rota publica usada em Devocionais.jsx). Quem ja'
            // esta logado(a) pula a landing e cai direto na leitura (ver o
            // useEffect de auto-skip em PrototypeApp.js); quem nao esta logado ve a
            // landing publica normal, com "Comecar a leitura" (pedido da Carla,
            // 25/10/2026: "deveria abrir direto o ebook se ja estiver logado").
            to={`/familia/ebooks/${featuredEbook.id}`}
            onClick={() => trackEvent("cta_click", { cta: "avesso_ebook_home", location: "home_banner" })}
            style={{ display: "flex", alignItems: "center", gap: 16, width: "100%", boxSizing: "border-box", margin: "28px 0 0", padding: 18, borderRadius: 18, border: "1px solid rgba(230,177,90,0.38)", background: "linear-gradient(135deg, rgba(230,177,90,0.14), rgba(15,23,42,0.94))", boxShadow: "0 16px 30px rgba(0,0,0,0.24)", textDecoration: "none", color: "inherit" }}
          >
            <div style={{ width: 56, height: 78, borderRadius: 8, overflow: "hidden", flexShrink: 0, background: featuredEbook.coverColor || "#333", boxShadow: "0 8px 18px rgba(0,0,0,0.35)" }}>
              {featuredEbook.coverImage && (
                <img src={featuredEbook.coverImage} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: "0 0 4px", fontSize: 10.5, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 900, color: "#E6B15A" }}>É a série que estamos pregando agora</p>
              <p style={{ margin: 0, fontSize: 18, fontWeight: 900, color: "#f8fbff" }}>{featuredEbook.title}</p>
              <p style={{ margin: "4px 0 0", fontSize: 12.5, lineHeight: 1.5, color: "#c7d1e4" }}>{featuredEbook.subtitle}</p>
            </div>
            <ChevronRight size={18} strokeWidth={2.2} color="#E6B15A" style={{ flexShrink: 0 }} />
          </NavLink>
        );
      })()}

      {allFeaturedEvents.length > 0 && (
        <div
          className="featured-events"
          style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "flex-start", gap: 16, margin: "28px 0" }}
        >
          {allFeaturedEvents.map((evento) => {
            const cardStyle = { display: "block", flex: "1 1 220px", maxWidth: 300, padding: 0, overflow: "hidden", borderRadius: 14, position: "relative" };
            const badge = evento.badge && (
              <span
                style={{
                  position: "absolute",
                  top: 10,
                  left: 10,
                  zIndex: 1,
                  background: evento.isToday ? "#E6B15A" : "rgba(10,14,24,0.82)",
                  color: evento.isToday ? "#151b2a" : "#fff",
                  fontSize: 12,
                  fontWeight: evento.isToday ? 900 : 700,
                  padding: evento.isToday ? "5px 12px" : "4px 10px",
                  borderRadius: 999,
                  letterSpacing: evento.isToday ? 0.6 : 0.3,
                  textTransform: evento.isToday ? "uppercase" : "none",
                  boxShadow: evento.isToday ? "0 4px 14px rgba(230,177,90,0.55)" : "0 2px 8px rgba(0,0,0,0.35)"
                }}
              >
                {evento.badge}
              </span>
            );
            // "padded": quando o arquivo de um banner vem
            // com o texto quase colado na borda inferior -- sem isso o card
            // (overflow hidden, padding 0) deixa o texto espremido contra o
            // canto arredondado. Em vez de recortar a arte, da uma folga
            // (fundo escuro igual ao tom da propria foto) so embaixo, pra
            // centralizar melhor o conteudo dentro do card.
            const imageEl = <img src={evento.image} alt={evento.alt} style={{ width: "100%", display: "block", aspectRatio: evento.ratio || undefined }} />;
            const image = evento.padded ? (
              <div style={{ background: "#05070c", paddingBottom: 18 }}>{imageEl}</div>
            ) : imageEl;

            // Pedido do Rodrigo: todos os banners abrem a arte ampliada no
            // clique, em vez de navegar pra /eventos -- antes so os banners
            // curados na mao (FEATURED_EVENTS) abriam assim (os recorrentes
            // ainda levavam pra /eventos), mas ele preferiu padronizar pra
            // todos, inclusive os que tem evento cadastrado la.
            return (
              <button
                key={evento.id}
                type="button"
                className="card featured-event-banner"
                style={{ ...cardStyle, textAlign: "left", font: "inherit", cursor: "zoom-in" }}
                onClick={() => {
                  trackEvent("cta_click", { cta: "evento_destaque_ampliar", location: "home_banner", evento: evento.id });
                  setLightboxImage({ src: evento.image, alt: evento.alt });
                }}
              >
                {badge}
                {image}
              </button>
            );
          })}
        </div>
      )}

      {lightboxImage && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Fechar imagem ampliada"
          onClick={() => setLightboxImage(null)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") setLightboxImage(null);
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(5,8,16,0.92)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            cursor: "zoom-out"
          }}
        >
          <button
            type="button"
            onClick={() => setLightboxImage(null)}
            aria-label="Fechar"
            style={{
              position: "absolute",
              top: 18,
              right: 18,
              width: 36,
              height: 36,
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.3)",
              background: "rgba(10,14,24,0.7)",
              color: "#fff",
              fontSize: 18,
              lineHeight: 1,
              cursor: "pointer"
            }}
          >
            ×
          </button>
          <img
            src={lightboxImage.src}
            alt={lightboxImage.alt}
            onClick={(event) => event.stopPropagation()}
            // 2a rodada de aumento (era min(92vw, 1000px)/90vh) -- mesmo
            // depois do primeiro ajuste (10/2026) o Rodrigo ainda via a arte
            // pequena ao ampliar, principalmente banners horizontais (16:9)
            // numa tela de celular alta: largura ja ia quase na borda, mas a
            // altura (que manda no aspecto 16:9) sobrava muito espaco escuro
            // em cima/embaixo. width:100% (nao so maxWidth) faz a imagem
            // crescer ate o teto disponivel em vez de ficar no tamanho
            // intrinseco do arquivo, e os tetos (97vw/96vh) usam quase a
            // tela inteira (Rodrigo, 25/10/2026: "abre tamanho maior esta
            // pequeno").
            style={{
              width: "100%",
              height: "auto",
              maxWidth: "min(97vw, 1400px)",
              maxHeight: "96vh",
              borderRadius: 16,
              boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
              cursor: "default"
            }}
          />
        </div>
      )}

      <div className="section-head" style={{ marginTop: 40 }}>
        <h2>Fique conectado</h2>
        <p>Acompanhe o conteúdo durante a semana e, se fizer sentido, contribua com o que Deus está fazendo aqui.</p>
      </div>
      <div className="grid grid-2" style={{ alignItems: "start" }}>
        <div className="card channels-panel">
          <ChannelsList title="Nos acompanhe" compact />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <button
            type="button"
            className="card donate-teaser barnabe-teaser"
            style={{ textAlign: "left", font: "inherit", cursor: "pointer", width: "100%" }}
            onClick={() => {
              trackEvent("cta_click", { cta: "falar_com_barnabe", location: "fique_conectado" });
              openBarnabeWidget();
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
              {/* Mesmo avatar do balaozinho de chat (servido pelo mosaico-api,
                  rota publica com CORS liberado pra isso -- ver src/routes/agente.js),
                  pra quem olha o card já reconhecer a carinha do Barnabé (pedido
                  do Rodrigo, 10/2026: "acho q podia ter uma foto do barnabe"). */}
              <img
                src={`${API_BASE}/agente/mosaico.png`}
                alt="Barnabé, o camaleão mascote da Mosaico"
                className="barnabe-avatar"
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p className="eyebrow" style={{ marginBottom: 0 }}>Fale com a gente</p>
                <h3>Converse com o Barnabé</h3>
                <p>Tire uma dúvida, peça oração ou só converse — nosso agente responde na hora, aqui mesmo no site.</p>
                <p className="meta">Abrir conversa →</p>
              </div>
            </div>
          </button>
          <NavLink className="card donate-teaser" to="/contato#give-card-section" onClick={() => trackEvent("cta_click", { cta: "contribuir", location: "fique_conectado" })}>
            <p className="eyebrow" style={{ marginBottom: 0 }}>Contribuição</p>
            <h3>Contribua com a Mosaico</h3>
            <p>Pix, sem pressa e sem pressão — só gratidão. Veja a chave e o QR code.</p>
            <p className="meta">Ver formas de contribuir →</p>
          </NavLink>
        </div>
      </div>
    </>
  );
}
